<script setup lang="ts">
import Dialog from 'primevue/dialog';
import { onBeforeUnmount, watch } from 'vue';
import shareIcon from '~/assets/images/icons/share.svg';

const TITLE_ID = 'ios-install-title';

const visible = defineModel<boolean>('visible', { default: false });

const emit = defineEmits<{ cancel: []; confirm: [] }>();

// PrimeVue's own escape handling would close the dialog directly, bypassing the
// dismissal the parent records on `cancel`. Routing the key through the same
// event keeps Escape and the Cancel button on one path.
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') emit('cancel');
}

function unbind() {
  document.removeEventListener('keydown', onKeydown);
}

watch(
  visible,
  (isVisible) => {
    if (!import.meta.client) return;
    if (isVisible) document.addEventListener('keydown', onKeydown);
    else unbind();
  },
  { immediate: true },
);

onBeforeUnmount(unbind);
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :draggable="false"
    :closable="false"
    :close-on-escape="false"
    :show-header="false"
    class="ios-install"
    :aria-labelledby="TITLE_ID"
  >
    <h2 :id="TITLE_ID" class="ios-install__title">Install VoteWise</h2>

    <ol class="ios-install__steps">
      <li class="ios-install__step" data-testid="install-step">
        1. Tap the
        <img
          :src="shareIcon"
          alt="Share"
          width="16"
          height="16"
          class="ios-install__share-icon"
        />
        <span class="ios-install__emphasis">Share</span> button on your browser
      </li>
      <li class="ios-install__step" data-testid="install-step">
        2. Scroll down and tap '<span class="ios-install__emphasis"
          >Add to Home Screen</span
        >'
      </li>
      <li class="ios-install__step" data-testid="install-step">
        3. Tap '<span class="ios-install__emphasis">Add</span>' in the top-right
        corner.
      </li>
    </ol>

    <template #footer>
      <div class="ios-install__actions">
        <button
          type="button"
          class="ios-install__button ios-install__button--outlined"
          data-testid="install-cancel"
          @click="$emit('cancel')"
        >
          Cancel
        </button>
        <button
          type="button"
          class="ios-install__button ios-install__button--filled"
          data-testid="install-confirm"
          @click="$emit('confirm')"
        >
          Got It
        </button>
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.ios-install {
  &__title {
    // font-title-6, not font-title-3: the design's 20/24 bold maps to
    // `font-title-6` here, while `font-title-3` is 48/48 — which rendered the
    // title at more than double size and pushed every margin below it down.
    @include font-title-6;
    text-align: center;
    color: $text-color-subtext;
  }

  &__steps {
    display: flex;
    flex-direction: column;
    gap: $spacing-md;
    margin-top: $spacing-2xl;
    // Tailwind's preflight strips list markers, and the design numbers the
    // steps in the copy itself — so no marker is wanted here either.
    list-style: none;
    padding: 0;
  }

  &__step {
    @include font-footnote;
    color: $text-color-subtext;
  }

  &__emphasis {
    @include font-footnote-bold;
  }

  &__share-icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    vertical-align: text-bottom;
  }

  &__actions {
    display: flex;
    gap: $spacing-sm;
    width: 100%;
  }

  &__button {
    @include font-subheadline-bold;
    // Equal halves regardless of label length, per the design.
    flex: 1 1 0;
    height: 40px;
    border-radius: $radius-sm;
    cursor: pointer;

    &--outlined {
      background-color: transparent;
      border: 1px solid $brand-color;
      color: $brand-color;
    }

    &--filled {
      background-color: $brand-color;
      border: 1px solid $brand-color;
      color: $text-color-inverted;
    }
  }
}
</style>

<!--
  Unscoped: themes the teleported PrimeVue dialog panel, which carries the
  `.ios-install` class but no scoped data-v attribute. Kept narrow to this
  modal so it does not leak into the location picker's sheet chrome.
-->
<style lang="scss">
.ios-install.p-dialog {
  // Figma's 343px sits on a 375px frame, i.e. a 16px inset each side. Expressed
  // as the inset rather than the width so the card stays near-full-width on
  // every phone — a literal 343px drifts to a 24px gutter on a 390px device and
  // wider still on a 430px one. Capped so it cannot stretch into an absurdly
  // wide card on large phones and tablets, where the banner still shows (the
  // cap only engages above a 432px viewport, so no phone is affected).
  width: calc(100vw - #{$spacing-lg * 2});
  max-width: 400px;
  border-radius: $radius-lg;
  background-color: $complementary-color-shade-3;

  .p-dialog-content,
  .p-dialog-footer {
    background-color: transparent;
    padding: 0 $spacing-lg;
  }

  .p-dialog-content {
    padding-top: $spacing-lg;
  }

  .p-dialog-footer {
    padding-top: $spacing-2xl;
    padding-bottom: $spacing-lg;
  }
}
</style>
